import React, {useEffect, useMemo} from "react";
import './App.css';
import {Navigate, Route, Routes} from "react-router-dom";
import {Provider, useDispatch, useSelector} from "react-redux";
import store from "./store/store";
import {createTheme, ThemeProvider} from "@mui/material";
import {fetchUserSettings, selectUserSettings} from "./store/userSettingsSlice";
import {
    DEFAULT_PRIMARY_INDEX,
    DEFAULT_SECONDARY_INDEX,
    getPrimaryColor,
    getSecondaryColor
} from "./theme/themePalettes";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import RecoveryPage from "./pages/RecoveryPage";
import TransactionsPage from "./pages/TransactionsPage";
import CurrenciesPage from "./pages/CurrenciesPage";
import CategoriesPage from "./pages/CategoriesPage";
import WalletsPage from "./pages/WalletsPage";
import BudgetsPage from "./pages/BudgetsPage";
import TemplatesPage from "./pages/TemplatesPage";
import ReportsPage from "./pages/ReportsPage";
import ReportsCategoriesPage from "./pages/ReportsCategoriesPage";
import ReportsCategoriesPieChartPage from "./pages/ReportsCategoriesPieChartPage";
import ReportsCategoriesBarChartPage from "./pages/ReportsCategoriesBarChartPage";
import ReportsCategoriesRadarChartPage from "./pages/ReportsCategoriesRadarChartPage";
import ReportsIncomeVsExpensePage from "./pages/ReportsIncomeVsExpensePage";
import ReportsIncomeVsExpenseLineChartPage from "./pages/ReportsIncomeVsExpenseLineChartPage";
import ReportsIncomeVsExpenseBarChartPage from "./pages/ReportsIncomeVsExpenseBarChartPage";
import ReportsCashFlowPage from "./pages/ReportsCashFlowPage";
import ReportsCashFlowBalancesPage from "./pages/ReportsCashFlowBalancesPage";
import ReportsCashFlowWaterfallPage from "./pages/ReportsCashFlowWaterfallPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import SetPasswordPage from "./pages/SetPasswordPage";
import SelectLocalePage from "./pages/SelectLocalePage";
import SettingsPage from "./pages/SettingsPage";

function ThemedApp() {
    const dispatch = useDispatch();
    const settings = useSelector(selectUserSettings);

    useEffect(() => {
        dispatch(fetchUserSettings());
    }, [dispatch]);

    const theme = useMemo(() => {
        const primary = getPrimaryColor(settings?.primaryColorIndex ?? DEFAULT_PRIMARY_INDEX);
        const secondaryMain = getSecondaryColor(settings?.secondaryColorIndex ?? DEFAULT_SECONDARY_INDEX);
        return createTheme({
            palette: {
                primary: {main: primary.main, dark: primary.dark},
                secondary: {main: secondaryMain},
            },
        });
    }, [settings?.primaryColorIndex, settings?.secondaryColorIndex]);

    return (
        <ThemeProvider theme={theme}>
            <Routes>
                <Route exact path='/' element={<Navigate to="/transactions" replace/>}/>
                <Route exact path='/signin' element={<LoginPage/>}/>
                <Route exact path='/registration' element={<RegisterPage/>}/>
                <Route exact path='/recovery' element={<RecoveryPage/>}/>
                <Route exact path='/setPassword' element={<SetPasswordPage/>}/>
                <Route exact path='/setPassword.html' element={<SetPasswordPage/>}/>
                <Route exact path='/select-locale' element={<SelectLocalePage/>}/>

                <Route exact path='/transactions' element={<TransactionsPage/>}/>
                <Route exact path='/currencies' element={<CurrenciesPage/>}/>
                <Route exact path='/categories' element={<CategoriesPage/>}/>
                <Route exact path='/wallets' element={<WalletsPage/>}/>
                <Route exact path='/budgets' element={<BudgetsPage/>}/>
                <Route exact path='/templates' element={<TemplatesPage/>}/>
                <Route exact path='/reports' element={<ReportsPage/>}/>
                <Route exact path='/reports/categories' element={<ReportsCategoriesPage/>}/>
                <Route exact path='/reports/categories/pieChart' element={<ReportsCategoriesPieChartPage/>}/>
                <Route exact path='/reports/categories/barChart' element={<ReportsCategoriesBarChartPage/>}/>
                <Route exact path='/reports/categories/radarChart' element={<ReportsCategoriesRadarChartPage/>}/>
                <Route exact path='/reports/income-vs-expense' element={<ReportsIncomeVsExpensePage/>}/>
                <Route exact path='/reports/income-vs-expense/lineChart' element={<ReportsIncomeVsExpenseLineChartPage/>}/>
                <Route exact path='/reports/income-vs-expense/barChart' element={<ReportsIncomeVsExpenseBarChartPage/>}/>
                <Route exact path='/reports/cash-flow' element={<ReportsCashFlowPage/>}/>
                <Route exact path='/reports/cash-flow/balances' element={<ReportsCashFlowBalancesPage/>}/>
                <Route exact path='/reports/cash-flow/waterfall' element={<ReportsCashFlowWaterfallPage/>}/>
                <Route exact path='/settings' element={<SettingsPage/>}/>
                <Route exact path='/privacy-policy' element={<PrivacyPolicyPage/>}/>
            </Routes>
        </ThemeProvider>
    );
}

function App() {
    return (
        <div>
            <Provider store={store}>
                <ThemedApp/>
            </Provider>
        </div>
    );
}

export default App;
